import { StationManagementModule } from './station-management/station-management.module';
import { IhmLoadingIndicatorModule } from '@ihm-software/ihm-ui-common-angular-10';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { CommonModule } from '@angular/common';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorIntl, MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSliderModule } from '@angular/material/slider';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialogModule } from '@angular/material/dialog'
import { BrowserModule, Title } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalRedirectComponent,
  MsalService,
} from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgProgressModule } from '@ngx-progressbar/core';
import { AgGridModule } from 'ag-grid-angular';
import { CellService, DynamicTableModule } from 'material-dynamic-table';
import { Ng2FlatpickrModule } from 'ng2-flatpickr';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';

import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CoreModule } from './core/core.module';
import { HttpHeaderInterceptor } from './core/http-header-interceptor';
import { DownloadsComponent } from './downloads/downloads.component';
import { AzureProfileAdminGuard } from './guards/azure-profile-admin.guard';
import { AzureProfileGuard } from './guards/azure-profile.guard';
import { HeaderComponent } from './header/header.component';
import { HomeComponent } from './home/home.component';
import { ProfileComponent } from './profile/profile.component';
import { ProfileService } from './profile/profile.service';
import { SearchDialogComponent } from './search-dialog/search-dialog.component';
import { SearchFiltersService } from './search-filters/search-filters.service';
import { EditCellComponent, MessageService } from './settings/edit-cell.component';
import { NotificationDialogComponent, SettingsComponent } from './settings/settings.component';
import { SharedModule } from './shared';
import { CustomMatPaginator } from './shared/custom-mat-paginator';
import { SidenavComponent } from './sidenav/sidenav.component';
import { SubscriptionsComponent } from './subscriptions/subscriptions.component';
import { LinearPlayoutComponent } from './linear-playout/linear-playout.component';
import { LinearPlayoutTableComponent } from './linear-playout/linear-playout-table/linear-playout-table.component';
import { ConfirmationDialogComponent } from './linear-playout/linear-playout-table/confirmation-dialog/confirmation-dialog.component';
import { AudioSubscriptionsComponent } from './audio-subscriptions/audio-subscriptions.component';


const isIE =
  window.navigator.userAgent.indexOf('MSIE ') > -1 ||
  window.navigator.userAgent.indexOf('Trident/') > -1;

export function loggerCallback(logLevel: LogLevel, message: string): void {
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {

      clientId: environment.clientId,
      authority: environment.authority,
      redirectUri: environment.redirectUri,
      postLogoutRedirectUri: environment.redirectUri,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: isIE,
    },
    system: {
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false,
      },
    },
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', ['user.read', 'openid', 'profile', 'email']);
   protectedResourceMap.set(environment.streamUrl, ['user.read', 'openid', 'profile', 'email']);
  protectedResourceMap.set(environment.stationUrl, ['user.read', 'openid', 'profile', 'email']);
  protectedResourceMap.set(environment.downloadsUrl, ['user.read', 'openid', 'profile', 'email']);
  protectedResourceMap.set(environment.subscriptionsUrl, ['user.read', 'openid', 'profile', 'email']);
  protectedResourceMap.set(environment.theraptorURL, ['user.read', 'openid', 'profile', 'email']);
  protectedResourceMap.set(environment.profileUrl, ['user.read', 'openid', 'profile', 'email']);
  protectedResourceMap.set(environment.usersUrl, ['user.read', 'openid', 'profile', 'email']);
  protectedResourceMap.set(environment.silenceLogsUrl, ['user.read', 'openid', 'profile', 'email']);
  protectedResourceMap.set(environment.logoUrlPrefix, ['user.read', 'openid', 'profile', 'email']);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ['user.read', 'openid', 'profile', 'email'],
    },
  };
}

@NgModule({
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  exports: [],
  declarations: [
    AppComponent,
    HeaderComponent,
    SidenavComponent,
    HomeComponent,
    SearchDialogComponent,
    ProfileComponent,
    SettingsComponent,
    DownloadsComponent,
    SubscriptionsComponent,
    NotificationDialogComponent,
    EditCellComponent,
    LinearPlayoutComponent,
    LinearPlayoutTableComponent,
    ConfirmationDialogComponent,
    AudioSubscriptionsComponent
   ],
  imports: [
    CommonModule,
    BrowserModule,
    BrowserAnimationsModule,
    NgbModule,
    Ng2FlatpickrModule,
    AppRoutingModule,
    SharedModule,
    NgProgressModule,
    HttpClientModule,
    CoreModule,
    DynamicTableModule,
    MatInputModule,
    MatTooltipModule,
    MatTableModule,
    MatPaginatorModule,
    MatButtonModule,
    MatSortModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatDialogModule, 
    FormsModule,
    ReactiveFormsModule,
    MatGridListModule,
    MatChipsModule,
    MatIconModule,
    MatCheckboxModule,
    InfiniteScrollModule,
    MatSelectModule,
    MatExpansionModule,
    MatSliderModule,
    MatSlideToggleModule,
    MatAutocompleteModule,
    MatTabsModule,
    AgGridModule.withComponents([]),
    MsalModule,
    MatSnackBarModule,
    MatProgressBarModule,
    MatBadgeModule,
    IhmLoadingIndicatorModule.forRoot(true),
    StationManagementModule
  ],
  providers: [
    HttpClientModule,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpHeaderInterceptor,
      multi: true
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    ProfileService,
    AzureProfileGuard,
    AzureProfileAdminGuard,
    Title,
    MessageService,
    SearchFiltersService,
    MsalInterceptor,
    {
      provide: MatPaginatorIntl,
      useClass: CustomMatPaginator
    },
  ],
  bootstrap: [AppComponent, MsalRedirectComponent],
  entryComponents: [
    SearchDialogComponent,
    NotificationDialogComponent,
    EditCellComponent,
  ]
})
export class AppModule {
  constructor(private readonly cellService: CellService) {
    cellService.registerCell('edit', EditCellComponent);
  }
}
